import { useState } from 'react';
import type { Patient } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';

interface MDVisitNoteSummaryProps {
  patient: Patient;
}

/**
 * Format an MD visit date using the dashboard display convention.
 *
 * @param value - Date string from the extraction
 * @returns A locale date string, the raw value, or an em dash
 */
function fmtDate(value?: string): string {
  return formatDisplayDate(value, '—');
}

export function MDVisitNoteSummary({ patient }: MDVisitNoteSummaryProps) {
  const visitNote = patient.mdVisitNote;

  // Check if timeline should be collapsed by default (more than 3 entries)
  const timelineEntries = visitNote?.treatmentTimeline || [];
  const shouldCollapseByDefault = timelineEntries.length > 3;

  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);

  // Calculate how many entries to show
  const entriesToShow = shouldCollapseByDefault && !isTimelineExpanded ? 3 : timelineEntries.length;

  if (!visitNote) {
    return (
      <div
        className="rounded-lg p-6 shadow-sm"
        style={{
          backgroundColor: 'var(--color-card)',
          border: '1px solid var(--color-border)',
        }}
      >
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          No MD visit note available for this patient.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg shadow-sm"
      style={{
        backgroundColor: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        padding: '1.5rem',
      }}
    >
      {/* Visit Header */}
      <div
        style={{
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
          <div className="flex items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
              }}
            >
              Visit Note Summary
            </span>
          </div>

          {/* Visit Type Badge */}
          <div
            style={{
              backgroundColor: '#EFF6FF',
              borderRadius: '0.375rem',
              padding: '0.5rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#3B82F6',
                flexShrink: '0',
              }}
            />
            <span
              style={{
                fontWeight: '500',
                fontSize: 'var(--font-size-sm)',
                color: '#1E40AF',
              }}
            >
              {visitNote.visitType}
            </span>
          </div>
        </div>

        {/* Visit Details */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.25rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Visit Date
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
              }}
            >
              {fmtDate(visitNote.visitDate)}
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.25rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Provider
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
              }}
            >
              {visitNote.provider || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Diagnosis Overview Section */}
      <div
        style={{
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.5rem',
          }}
        >
          {/* Row 1 */}
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Primary diagnosis
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.diagnosis}
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Histologic type
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.histology || 'N/A'}
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Diagnosis date
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {fmtDate(patient.diagnosisDate)}
            </p>
          </div>

          {/* Row 2 */}
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Initial TNM stage
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.tnmClassification || 'N/A'} ({patient.stage})
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Current TNM stage
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.tnmClassification || 'N/A'} ({patient.stage})
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Metastatic status
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.metastaticSites && patient.metastaticSites.length > 0 ? 'Metastatic disease' : 'No metastasis'}
            </p>
          </div>

          {/* Row 3 */}
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Metastatic sites
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.metastaticSites && patient.metastaticSites.length > 0
                ? patient.metastaticSites.join(', ')
                : 'None'}
            </p>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Disease Status
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
                lineHeight: '1.4',
              }}
            >
              {patient.diseaseStatus || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Treatment & Stage Evolution Timeline */}
      {timelineEntries.length > 0 && (
        <div
          style={{
            marginBottom: '1.5rem',
            paddingBottom: '1.5rem',
          }}
        >
          {/* Timeline Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: 'var(--color-border)',
                top: '50%',
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'var(--color-card)',
                padding: '0 1rem',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <span
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                }}
              >
                Treatment & Stage Evolution Timeline
              </span>
              {shouldCollapseByDefault && (
                <button
                  type="button"
                  onClick={() => {
                    setIsTimelineExpanded((prev) => !prev);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid transparent',
                    cursor: 'pointer',
                    padding: '0.375rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-text-primary)',
                    borderRadius: '0.25rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-muted)';
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      transform: isTimelineExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      pointerEvents: 'none',
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Timeline Entries */}
          <div style={{ position: 'relative' }}>
            {/* Vertical Timeline Line */}
            <div
              style={{
                position: 'absolute',
                left: '20px',
                top: '20px',
                bottom: '60px',
                width: '2px',
                backgroundColor: 'var(--color-border)',
              }}
            />

            {/* Timeline Items */}
            {timelineEntries.slice(0, entriesToShow).map((entry, index) => {
              const borderColor = entry.isCurrent ? '#EF4444' : index === 1 ? '#F59E0B' : '#3B82F6';
              const dotColor = entry.isCurrent ? '#DC2626' : index === 1 ? '#D97706' : '#2563EB';

              return (
                <div
                  key={index}
                  style={{
                    border: `2px solid ${borderColor}`,
                    borderRadius: '0.5rem',
                    padding: '0.875rem',
                    marginBottom: '0.75rem',
                    backgroundColor: 'var(--color-card)',
                    position: 'relative',
                    marginLeft: '40px',
                  }}
                >
                  {/* Timeline Dot */}
                  <div
                    style={{
                      position: 'absolute',
                      left: '-42px',
                      top: '20px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: dotColor,
                      border: '3px solid var(--color-card)',
                      zIndex: 1,
                    }}
                  />

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr 1fr',
                      gap: '1rem',
                    }}
                  >
                    {/* Left Column - Stage Info */}
                    <div>
                      <span
                        style={{
                          backgroundColor: entry.isCurrent ? '#DC2626' : dotColor,
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '500',
                          marginBottom: '0.5rem',
                          display: 'inline-flex',
                          fontFamily: 'Lato, sans-serif',
                        }}
                      >
                        {entry.isCurrent ? 'Current Status' : fmtDate(entry.date)}
                      </span>
                      {/* Stage / TNM are patient-level (same across lines) — show ONLY on the
                          current card so the timeline doesn't repeat the same stage on every row. */}
                      {entry.isCurrent && entry.stage && (
                        <h4
                          style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: '700',
                            color: 'var(--color-text-primary)',
                            margin: '0 0 0.375rem 0',
                            fontFamily: 'Lato, sans-serif',
                          }}
                        >
                          {entry.stage}
                        </h4>
                      )}
                      {entry.isCurrent && entry.tnmClassification && (
                        <p
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-primary)',
                            margin: '0 0 0.375rem 0',
                            fontFamily: 'Lato, sans-serif',
                          }}
                        >
                          {entry.tnmClassification}
                        </p>
                      )}
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          margin: '0',
                          fontFamily: 'Lato, sans-serif',
                        }}
                      >
                        {entry.responseStatus}
                      </p>
                    </div>

                    {/* Middle Column - Regimen & Toxicities */}
                    <div>
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '600',
                          color: 'var(--color-text-muted)',
                          margin: '0 0 0.375rem 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Regimen
                      </p>
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '500',
                          color: 'var(--color-text-primary)',
                          margin: '0 0 0.75rem 0',
                          lineHeight: '1.4',
                          fontFamily: 'Lato, sans-serif',
                        }}
                      >
                        {entry.regimen}
                      </p>

                      {entry.toxicities && entry.toxicities.length > 0 && (
                        <>
                          <p
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600',
                              color: 'var(--color-text-muted)',
                              margin: '0 0 0.375rem 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Toxicities
                          </p>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.5rem',
                            }}
                          >
                            {entry.toxicities.map((toxicity, idx) => (
                              <span
                                key={idx}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: '#FEF3C7',
                                  color: '#92400E',
                                  borderRadius: '0.25rem',
                                  fontSize: 'var(--font-size-xs)',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  fontFamily: 'Lato, sans-serif',
                                }}
                              >
                                {toxicity}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Right Column - Key Findings */}
                    <div>
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '600',
                          color: 'var(--color-text-muted)',
                          margin: '0 0 0.375rem 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Key Findings
                      </p>
                      <ul
                        style={{
                          margin: '0',
                          paddingLeft: '1rem',
                          listStyleType: 'disc',
                        }}
                      >
                        {entry.keyFindings.map((finding, idx) => (
                          <li
                            key={idx}
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-primary)',
                              marginBottom: '0.25rem',
                              lineHeight: '1.5',
                              fontFamily: 'Lato, sans-serif',
                            }}
                          >
                            {finding}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show More/Less Button */}
          {shouldCollapseByDefault && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  setIsTimelineExpanded((prev) => !prev);
                }}
                style={{
                  backgroundColor: 'var(--color-muted)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily: 'Lato, sans-serif',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {isTimelineExpanded ? 'Show Less' : `Show All ${timelineEntries.length} Entries`}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: isTimelineExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    pointerEvents: 'none',
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          )}

          {/* Duration Since Diagnosis */}
          {timelineEntries.length > 0 && (
            <div
              style={{
                textAlign: 'center',
                marginTop: '1.5rem',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
                fontFamily: 'Lato, sans-serif',
              }}
            >
              <strong>Duration since diagnosis:</strong>{' '}
              {(() => {
                const diagnosisEntry = timelineEntries[timelineEntries.length - 1];
                const diagnosisDate = new Date(diagnosisEntry.date);
                if (Number.isNaN(diagnosisDate.getTime())) return '—';
                const today = new Date();
                const diffTime = Math.abs(today.getTime() - diagnosisDate.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                const years = Math.floor(diffDays / 365);
                const months = Math.floor((diffDays % 365) / 30);

                if (years > 0) {
                  return months > 0
                    ? `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`
                    : `${years} year${years > 1 ? 's' : ''}`;
                } else {
                  return `${months} month${months > 1 ? 's' : ''}`;
                }
              })()}
            </div>
          )}
        </div>
      )}

      {/* Medical Comorbidities */}
      {visitNote.comorbidities && visitNote.comorbidities.length > 0 && (
        <div
          style={{
            marginBottom: '1.5rem',
            paddingBottom: '1.5rem',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <h3
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                margin: '0',
                fontFamily: 'Lato, sans-serif',
              }}
            >
              Medical Comorbidities
            </h3>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.75rem',
            }}
          >
            {visitNote.comorbidities.map((comorbidity, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: '#FAFBFC',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.5rem',
                  padding: '0.875rem 1rem',
                  fontFamily: 'Lato, sans-serif',
                }}
              >
                <h4
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    margin: '0 0 0.625rem 0',
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  {comorbidity.condition}
                </h4>

                {comorbidity.description && (
                  <ul
                    style={{
                      margin: '0 0 0.625rem 0',
                      paddingLeft: '1.25rem',
                      listStyleType: 'disc',
                    }}
                  >
                    <li
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        lineHeight: '1.5',
                        fontFamily: 'Lato, sans-serif',
                      }}
                    >
                      {comorbidity.description}
                    </li>
                  </ul>
                )}

                {comorbidity.medications && comorbidity.medications.length > 0 && (
                  <>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '600',
                        color: 'var(--color-text-muted)',
                        margin: '0 0 0.375rem 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontFamily: 'Lato, sans-serif',
                      }}
                    >
                      Current Medications:
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                      }}
                    >
                      {comorbidity.medications.map((medication, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#F3F4F6',
                            color: 'var(--color-text-primary)',
                            borderRadius: '0.25rem',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: '500',
                            display: 'inline-flex',
                            fontFamily: 'Lato, sans-serif',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          {medication}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
